import { Component } from '@angular/core'
import {
    Action,
    Column,
    selfValuesFactory,
    stringValuesToMap,
    Via,
    combineLib,
    libArrayValuesToMap,
    arrayComparatorFactory,
    compareDates,
    TableControlType,
    Table,
    FluentTracker,
    FieldTracker,
    isPositiveInt,
    HandbookService,
    EnvironmentService,
    entriesToMap,
    ConfirmationService
} from '@surgetech-eu/core'
import { of, Observable, Subscriber } from 'rxjs'
import { MaterialDetailsService, MaterialDetails } from '../services/material-details.service'

import moment from 'moment'
import uuid from 'uuid'
import { map, tap } from 'rxjs/operators'

const maxRank = 2147483647

@Component({
    selector: 'app-item-material-certification',
    templateUrl: './material-details.component.html',
    styleUrls: ['./material-details.component.scss'],
    standalone: false
})
export class MaterialDetailsComponent {
    private table: Table

    private mandatoryCertifications: any

    saving = false
    saved = false

    error: string = null

    private fluentTracker: FluentTracker
    private pendingSource: MaterialDetails[] = []

    constructor(
        private materialDetailsService: MaterialDetailsService,
        private handbookService: HandbookService,
        private environment: EnvironmentService,
        private confirmationService: ConfirmationService
    ) {
        this.handbookService.get().subscribe(data => {
            const organizationName = this.environment.get().organization
            this.mandatoryCertifications = data.get(organizationName).get('caseItemCertificationType').filter(x => !x.disabled)
        })
        this.fluentTracker = new FluentTracker(new FieldTracker(), {
            fields: {
                id: {
                    ns: true
                },
                key: {
                    required: true
                },
                description: {
                    required: true
                },
                mandatoryCertificates: {
                    required: false
                },
                disabled: {
                    required: false
                },
                rank: {
                    required: false,
                    validate: (value, extra) => {
                        if (!isPositiveInt(value)) {
                            return 'Should be a positive number'
                        }
                        if (value > maxRank) {
                            return `It must be less than ${maxRank}`
                        }
                        return null
                    }
                },
                new: {
                    required: false
                },
                fieldsToReset: {
                    required: false
                }
            }
        })
        this.fluentTracker.listen(FluentTracker.EVENT_CHANGE, () => (this.error = null), true)
    }

    onTableDelegateBind(table: Table): void {
        this.table = table
    }

    disableSupportQuery(): boolean {
        return true
    }

    getTableColumns(): Column[] {
        return [
            {
                name: 'key',
                title: 'Key',
                filter: {
                    via: Via.TEXT
                },
                control: {
                    type: TableControlType.INPUT,
                    changing: (value, row) => {
                        const indexToUpdate = this.pendingSource.findIndex(mc => mc.id === row.id)
                        if (indexToUpdate !== -1) {
                            this.pendingSource[indexToUpdate].key = value
                        }
                        this.fluentTracker.update(row.id, 'key', value)
                        this.fluentTracker.update(row.id, 'new', true)
                    },
                    canShow: (value, row) => row.new,
                    failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, 'key'),
                    failure: (v, row) => this.fluentTracker.tracker.getError(row.id, 'key'),
                    seed: row => this.fluentTracker.tracker.getValue(row.id, 'key')
                }
            },
            {
                name: 'description',
                title: 'Description',
                filter: {
                    via: Via.TEXT
                },
                control: {
                    type: TableControlType.INPUT,
                    changing: (value, row) => {
                        const indexToUpdate = this.pendingSource.findIndex(mc => mc.id === row.id)
                        if (indexToUpdate !== -1) {
                            this.pendingSource[indexToUpdate].description = value
                        }
                        this.fluentTracker.update(row.id, 'description', value)
                    },
                    canShow: (value, row) => row.new,
                    failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, 'description'),
                    failure: (v, row) => this.fluentTracker.tracker.getError(row.id, 'description'),
                    seed: row => this.fluentTracker.tracker.getValue(row.id, 'description')
                }
            },
            {
                name: 'mandatoryCertificates',
                title: 'Mandatory Certification',
                pipe: e => e.map(combineLib),
                filter: {
                    via: Via.SELFPICKER,
                    selfValues: selfValuesFactory('mandatoryCertificates', libArrayValuesToMap),
                    comparator: arrayComparatorFactory(v => (v ? v.map(item => item.id) : []))
                },
                control: {
                    type: TableControlType.PICKER,
                    values: () => entriesToMap(this.mandatoryCertifications),
                    change: (value, row) => {
                        this.fluentTracker.update(row.id, 'mandatoryCertificates', value)
                    },
                    canShow: (value, row) => row.new,
                    failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, 'mandatoryCertificates'),
                    failure: (v, row) => this.fluentTracker.tracker.getError(row.id, 'mandatoryCertificates'),
                    seed: row => this.fluentTracker.tracker.getValue(row.id, 'mandatoryCertificates')
                }
            },
            {
                name: 'createdAt',
                title: 'Entry Date',
                //                 pipe: v => (v ? moment(v).format(DD/MM/YYYY) : ),
                filter: {
                    via: Via.MOMENT
                },
                sorting: {
                    sortable: true,
                    compare: compareDates('createdAt')
                }
            },
            {
                name: 'createdBy',
                title: 'User',
                filter: {
                    via: Via.SELF,
                    selfValues: selfValuesFactory('createdBy', stringValuesToMap),
                    path: 'createdBy'
                }
            },
            {
                name: 'updatedAt',
                title: 'Date of Last Update',
                //                 pipe: v => (v ? moment(v).format(DD/MM/YYYY) : ),
                filter: {
                    via: Via.MOMENT
                },
                sorting: {
                    sortable: true,
                    compare: compareDates('updatedAt')
                }
            },
            {
                name: 'updatedBy',
                title: 'User Last Update',
                filter: {
                    via: Via.SELF,
                    selfValues: selfValuesFactory('updatedBy', stringValuesToMap),
                    path: 'updatedBy'
                }
            },
            {
                name: 'rank',
                path: 'rank',
                title: 'Rank',
                sorting: {
                    sortable: false
                },
                control: {
                    type: TableControlType.INPUT,
                    changing: (value, row) => {
                        if (!value && !row.new) {
                            this.addFieldToReset(row.id, 'rank')
                        } else {
                            this.fluentTracker.update(row.id, 'rank', value)
                            this.removeFieldFromReset(row.id, 'rank')
                        }
                        if (!this.fluentTracker.tracker.getValue(row.id, 'key')) {
                            this.fluentTracker.update(row.id, 'key', row.key)
                        }
                        if (!this.fluentTracker.tracker.getValue(row.id, 'description')) {
                            this.fluentTracker.update(row.id, 'description', row.description)
                        }
                    },
                    canShow: (value, row) => true,
                    failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, 'rank'),
                    failure: (v, row) => this.fluentTracker.tracker.getError(row.id, 'rank'),
                    seed: row => {
                        if (this.hasFieldToReset(row.id, 'rank')) {
                            return null
                        }

                        return this.fluentTracker.tracker.getValue(row.id, 'rank') || row.rank
                    }
                }
            },
            {
                name: 'disabled',
                title: 'Disable',
                sorting: {
                    sortable: false
                },
                control: {
                    type: TableControlType.DROPDOWN,
                    values: new Map<string, string>([
                        ['true', 'Disabled'],
                        ['false', 'Enabled']
                    ]),
                    required: true,
                    change: (value, row) => {
                        this.fluentTracker.update(row.id, 'disabled', value)
                        this.fluentTracker.update(row.id, 'key', row.key)
                        this.fluentTracker.update(row.id, 'description', row.description)
                    },
                    disabled: (v, r) => false,
                    canShow: (v, r) => true,
                    seed: row => this.fluentTracker.tracker.getValue(row.id, 'disabled') || row.disabled.toString()
                }
            }
        ]
    }

    canTableRemove(): boolean {
        return true
    }

    configureTableRemoveAction(action: Action) {
        action.disabled = (row: MaterialDetails) => row.used
        action.run = (row: MaterialDetails) => {
            return new Observable(observer => {
                const confirmation = this.confirmationService.openWithParams({
                    text: 'Material Detail has never been used for Case Items. Are you sure that you want to delete this entry?',
                    title: 'Delete'
                })
                confirmation
                    .then(() => {
                        this.error = null
                        this.saved = false

                        if (row.new) {
                            this.removeItem(row.id)
                            return of(true)
                        }
                        return this.materialDetailsService.remove(row.key).subscribe(
                            data => {
                                this.removeItem(row.id)
                                observer.next(data)
                            },
                            error => {
                                this.error = error
                            }
                        )
                    })
                    .catch(e => {
                        observer.next(true)
                    })
            })
        }
    }

    getTableData(): Observable<MaterialDetails[]> {
        if (this.pendingSource.length) {
            return of(this.pendingSource)
        }

        return this.materialDetailsService.getAll().pipe(
            map(data =>
                data.map(materialDetail => {
                    return {
                        ...materialDetail,
                        id: uuid()
                    }
                })
            ),
            tap(data => (this.pendingSource = [...data]))
        )
    }

    canSave(): boolean {
        return this.fluentTracker.hasValues() && !this.fluentTracker.hasErrors() && !this.saving
    }

    private removeFieldFromReset(ns: string, fieldName: string) {
        const fieldsToReset = this.fluentTracker.tracker.getValue(ns, 'fieldsToReset')
        if (fieldsToReset && fieldsToReset.includes(fieldName)) {
            fieldsToReset.splice(fieldsToReset.indexOf(fieldName))
            this.fluentTracker.update(ns, 'fieldsToReset', fieldsToReset)
        }
    }

    private addFieldToReset(ns: string, fieldName: string): void {
        const fieldsToReset = this.fluentTracker.tracker.getValue(ns, 'fieldsToReset')
        if (!fieldsToReset) {
            this.fluentTracker.update(ns, 'fieldsToReset', [fieldName])
        }
        if (fieldsToReset && !fieldsToReset.includes(fieldName)) {
            this.fluentTracker.update(ns, 'fieldsToReset', fieldsToReset.push(fieldName))
        }
    }

    private hasFieldToReset(ns: string, fieldName: string) {
        const fieldsToReset = this.fluentTracker.tracker.getValue(ns, 'fieldsToReset') || []

        return fieldsToReset.indexOf(fieldName) > -1
    }

    private duplicationKeysValidation(): void {
        const keys = this.pendingSource
        const result = keys.reduce(
            (acc, key) => {
                const i = acc.uniq.findIndex(x => {
                    return x.key.toLowerCase() === key.key.toLowerCase()
                })
                if (i <= -1) {
                    acc.uniq.push(key)
                } else {
                    acc.duplications.push(key)
                }
                return acc
            },
            {
                uniq: [],
                duplications: []
            }
        )
        if (result.duplications) {
            result.duplications.forEach(item => {
                this.fluentTracker.tracker.addError(item.id, 'key', 'Should have a unique value')
            })
        }
    }

    private convertPayload(): MaterialDetails[] {
        this.duplicationKeysValidation()
        const fluentTracker = this.fluentTracker.generate()
        console.log(this.fluentTracker)
        if (!this.fluentTracker.hasErrors()) {
            const data = []
            fluentTracker.forEach(item => {
                if (item.mandatoryCertificates) {
                    item.mandatoryCertificates = item.mandatoryCertificates.map(mc => {
                        const test = this.mandatoryCertifications.find(el => el.value === mc)
                        return {
                            id: test.value,
                            code: test.title
                        }
                    })
                }
                if (item.new) {
                    delete item.new
                }
                data.push({ ...item })
            })
            return data
        } else {
            this.table.refresh()
            return []
        }
    }

    save(): void {
        const data = this.convertPayload()
        this.error = null
        this.saved = false
        if (data.length) {
            this.saving = true
            this.materialDetailsService.createOrUpdate(data).subscribe(
                response => {
                    this.pendingSource = []
                    this.table.reload(
                        new Subscriber(() => {
                            this.fluentTracker.clearAll()
                            this.saving = false
                            this.saved = true
                        })
                    )
                },
                error => {
                    this.saving = false
                    this.error = error
                }
            )
        }
    }

    private createNewMaterialDetails(): MaterialDetails {
        const id = uuid()
        return {
            key: '',
            description: '',
            mandatoryCertificates: [],
            disabled: false,
            rank: null,
            id,
            new: true
        }
    }

    addNewItem(): void {
        this.pendingSource.splice(this.pendingSource.length + 1, 0, this.createNewMaterialDetails())
        this.table.reload()
        this.table.goToLastPage()
    }

    private removeItem(id: string) {
        this.fluentTracker.tracker.destroy(id)
        const removedIndex = this.pendingSource.findIndex(md => md.id === id)
        this.pendingSource.splice(removedIndex, 1)

        this.table.reload()
    }
}
